import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useForm, FormProvider } from 'react-hook-form';
import { useSelector } from 'react-redux';
import clsx from 'clsx';
import { makeStyles, useMediaQuery, useTheme } from '@material-ui/core';
import { CustomizedTypography } from '@components/Customized';
import { RootState } from '@modules/index';
import { getMinDateFromToday } from '@utils/date';
import useQueryString from '@hooks/useQueryString';
import useRequest from '@hooks/useRequest';
import {
  CapaPcbServiceType,
  PcbServiceInput,
  RegisterRfqInput,
  RegisterRfqPcbInput,
  RfqPcbAssemblyFileInput,
  RfqPcbFabricationFileInput,
  RfqReferenceFileInput
} from '@generated/graphql';
import {
  ReferenceFileInput,
  DetailedRequestInput,
  ProjectNameInput,
  ProjectPurposeSelect,
  DueDateInput,
  EstimateBudgetRangeInput,
  CircuitFabricationQuantityInput,
  PublicOpenControl,
  CircuitAssemblyMethodControl,
  CircuitAssemblyMetalMaskAvailablityControl,
  CircuitAssemblyMaterialSupplyMethodControl,
  CircuitFabricationFileInput,
  CircuitAssemblyFileInput,
  CircuitAssemblyQuantityInput
} from '../../FormComponents';
import { RequestQueryString } from '../../types';
import { PCBRequestProps } from './PCBProcessSelect';
import ConsultMethodControl from '@routes/NewRequest/FormComponents/ConsultMethodControl';

const PCBRequestForm: React.FC<PCBRequestProps> = ({
  setDisabledNextButton,
  nextButtonAction
}) => {
  const fabricationFiles = useSelector<RootState, RfqPcbFabricationFileInput[]>(
    (state) => state.request.pcb?.fabricationFiles ?? []
  );
  const assemblyFiles = useSelector<RootState, RfqPcbAssemblyFileInput[]>(
    (state) => state.request.pcb?.assemblyFiles ?? []
  );
  const referenceFiles = useSelector<RootState, RfqReferenceFileInput[]>(
    (state) => state.request.referenceFiles ?? []
  );
  const [isValid, setIsValid] = useState<boolean>(false);
  const theme = useTheme();
  const matches = useMediaQuery(theme.breakpoints.down('sm'));
  const {
    query: { process, serviceType }
  } = useQueryString<RequestQueryString>();
  const { requestContainer } = usePCBRequestStyles();
  const { requestState, onUpdateRfqInfo } = useRequest();
  const processes = process?.split(',').filter((service) => !!service);
  const hasCircuitDesign = processes?.includes(
    CapaPcbServiceType.CircuitDesign
  );
  const hasCircuitFabrication = processes?.includes(
    CapaPcbServiceType.CircuitFabrication
  );
  const hasCircuitAssembly = processes?.includes(
    CapaPcbServiceType.CircuitAssembly
  );
  const currentFabricationFiles = useRef<any[]>([]);
  const currentAssemblyFiles = useRef<any[]>([]);
  const formProps = useForm<RegisterRfqInput>({
    mode: 'onChange',
    shouldFocusError: true,
    defaultValues: {
      ...requestState,
      dueDate:
        requestState?.dueDate !== ''
          ? requestState?.dueDate
          : getMinDateFromToday(7).toISOString(),
      pcb: {
        ...(requestState.pcb as RegisterRfqPcbInput),
        metalMaskAvailablity: requestState.pcb?.metalMaskAvailablity ?? true
      }
    }
  });
  const { register, errors, watch } = formProps;
  const watchForm = watch();
  const { pcb } = errors as any;
  const currentFormValue = useRef<RegisterRfqInput>({
    ...requestState,
    referenceFiles,
    dueDate:
      requestState?.dueDate !== ''
        ? requestState?.dueDate
        : getMinDateFromToday(7).toISOString(),
    pcb: {
      ...requestState.pcb,
      services: requestState.pcb?.services ?? [],
      metalMaskAvailablity: requestState.pcb?.metalMaskAvailablity ?? true
    }
  });

  const validateForm = useCallback(() => {
    if (hasCircuitDesign) {
      if (!watchForm.detailedRequest) {
        setIsValid(false);
        return;
      }
      if (hasCircuitFabrication && !watchForm.pcb?.fabricationQuantity) {
        setIsValid(false);
        return;
      }
      if (hasCircuitAssembly && !watchForm.pcb?.assemblyQuantity) {
        setIsValid(false);
        return;
      }
    }

    if (!hasCircuitDesign && (hasCircuitFabrication || hasCircuitAssembly)) {
      if (fabricationFiles.length === 0) {
        setIsValid(false);
        return;
      }

      if (
        hasCircuitFabrication &&
        fabricationFiles.some((file) => !file.quantity || !file.thickness)
      ) {
        setIsValid(false);
        return;
      }
    }

    if (!hasCircuitDesign && hasCircuitAssembly) {
      if ((watchForm.pcb?.assemblyMethod ?? []).length === 0) {
        setIsValid(false);
        return;
      }

      if (assemblyFiles.length === 0) {
        setIsValid(false);
        return;
      }
      if (assemblyFiles.some((file) => !file.quantity)) {
        setIsValid(false);
        return;
      }
    }

    const {
      projectName,
      projectPurpose,
      dueDate,
      maxEstimatedBudget
    } = watchForm;

    if (
      projectName &&
      projectName.length <= 20 &&
      projectPurpose &&
      dueDate &&
      maxEstimatedBudget
    ) {
      setIsValid(true);
    } else {
      setIsValid(false);
    }
  }, [
    hasCircuitAssembly,
    hasCircuitDesign,
    hasCircuitFabrication,
    assemblyFiles,
    fabricationFiles,
    watchForm
  ]);

  const onSubmit = useCallback(() => {
    onUpdateRfqInfo({
      ...currentFormValue.current,
      drawingFiles: [],
      pcb: {
        ...currentFormValue.current.pcb,
        services:
          processes?.map(
            (service) => ({ serviceType: service } as PcbServiceInput)
          ) ?? [],
        assemblyFiles: hasCircuitDesign ? [] : currentAssemblyFiles.current,
        fabricationFiles: hasCircuitDesign
          ? []
          : currentFabricationFiles.current
      }
    });
  }, [processes, onUpdateRfqInfo, hasCircuitDesign]);

  useEffect(() => {
    currentFormValue.current = {
      ...watchForm,
      referenceFiles,
      drawingFiles: [],
      pcb: {
        ...watchForm.pcb,
        services:
          processes?.map(
            (service) => ({ serviceType: service } as PcbServiceInput)
          ) ?? [],
        assemblyFiles: hasCircuitDesign ? [] : assemblyFiles,
        fabricationFiles: hasCircuitDesign ? [] : fabricationFiles
      }
    };
  }, [
    watchForm,
    assemblyFiles,
    fabricationFiles,
    hasCircuitDesign,
    processes,
    requestState.drawingFiles,
    referenceFiles
  ]);

  useEffect(() => {
    nextButtonAction.current.PCB = onSubmit;
  }, [onSubmit, nextButtonAction]);

  useEffect(() => {
    if (!setDisabledNextButton) return;

    setDisabledNextButton(!isValid);
  }, [isValid, setDisabledNextButton]);

  useEffect(() => {
    validateForm();
    //eslint-disable-next-line
  }, [watch, validateForm, assemblyFiles, fabricationFiles]);

  useEffect(() => {
    return onSubmit;
    //eslint-disable-next-line
  }, []);

  return (
    <FormProvider {...formProps}>
      <form>
        <input
          type="hidden"
          ref={register({
            required: true
          })}
          name="serviceType"
          value={serviceType}
        />
        <div className={clsx([requestContainer, 'request-container'])}>
          {hasCircuitDesign && (
            <div className={clsx(['form-section'])}>
              <CustomizedTypography
                variant={!matches ? 'h2' : 'h3'}
                fontWeight="700"
                color="textPrimary"
                gutterBottoms={24}
              >
                설계정보를 입력해주세요
              </CustomizedTypography>

              <DetailedRequestInput
                ref={register}
                error={errors.detailedRequest?.message}
              />
              <ReferenceFileInput />
            </div>
          )}

          {(hasCircuitFabrication ||
            (!hasCircuitDesign && hasCircuitAssembly)) && (
            <div className={clsx(['form-section'])}>
              <CustomizedTypography
                variant={!matches ? 'h2' : 'h3'}
                fontWeight="700"
                color="textPrimary"
                gutterBottoms={24}
              >
                제작정보를 입력해주세요
              </CustomizedTypography>

              {!hasCircuitDesign && (
                <>
                  <CircuitFabricationFileInput
                    filesRef={currentFabricationFiles}
                  />
                  <PublicOpenControl />
                </>
              )}
              {hasCircuitDesign && hasCircuitFabrication && (
                <CircuitFabricationQuantityInput
                  error={pcb?.fabricationQuantity?.message}
                  ref={register}
                />
              )}
            </div>
          )}

          {hasCircuitAssembly && (
            <div className={clsx(['form-section'])}>
              <CustomizedTypography
                variant={!matches ? 'h2' : 'h3'}
                fontWeight="700"
                color="textPrimary"
                gutterBottoms={24}
              >
                조립정보를 입력해주세요
              </CustomizedTypography>

              {!hasCircuitDesign && (
                <>
                  <CircuitAssemblyFileInput filesRef={currentAssemblyFiles} />
                  <CircuitAssemblyMethodControl
                    error={pcb?.['assemblyMethod']?.message}
                  />
                  <CircuitAssemblyMetalMaskAvailablityControl
                    error={pcb?.['metalMaskAvailablity']?.message}
                  />
                  <CircuitAssemblyMaterialSupplyMethodControl
                    error={pcb?.['materialSupplyMethod']?.message}
                  />
                </>
              )}
              {hasCircuitDesign && (
                <CircuitAssemblyQuantityInput
                  error={pcb?.['assemblyQuantity']?.message}
                />
              )}
            </div>
          )}

          <div className={clsx(['form-section'])}>
            <CustomizedTypography
              variant={!matches ? 'h2' : 'h3'}
              fontWeight="700"
              color="textPrimary"
              gutterBottoms={24}
            >
              프로젝트 정보를 입력해주세요
            </CustomizedTypography>

            <ProjectNameInput
              error={errors.projectName?.message}
              ref={register({
                required: '프로젝트명을 입력해주세요.',
                validate: {
                  maxLength: (e) =>
                    e?.length > 20 ? '20자 이내로 입력해주세요' : true
                }
              })}
            />
            <ProjectPurposeSelect error={errors.projectPurpose?.message} />
            <DueDateInput error={errors.dueDate?.message} />
            <EstimateBudgetRangeInput
              maxError={errors?.maxEstimatedBudget?.message}
            />

            {!hasCircuitDesign && (
              <>
                <DetailedRequestInput
                  ref={register}
                  error={errors.detailedRequest?.message}
                />
                <ReferenceFileInput />
              </>
            )}
          </div>
          <div className={clsx(['form-section'])}>
            <CustomizedTypography
              variant={!matches ? 'h2' : 'h3'}
              fontWeight="700"
              color="textPrimary"
              gutterBottoms={24}
            >
              상담 시작 방식을 선택해주세요
            </CustomizedTypography>

            <ConsultMethodControl />
          </div>
        </div>
      </form>
    </FormProvider>
  );
};

export default PCBRequestForm;

const usePCBRequestStyles = makeStyles((theme) => ({
  requestContainer: {
    width: '100%',
    display: 'flex',
    flexDirection: 'column',
    gap: '72px',
    [theme.breakpoints.down('sm')]: {
      paddingTop: 0,
      paddingBottom: '32px'
    }
  }
}));
